import {
  Button,
  IconFrameButton,
  LinkBlock,
  LogoImg,
  logoMetadata,
} from '@cleanlab/design-system/components'
import { IconExternalLink, IconMoon, IconSun } from '@cleanlab/design-system/icons'
import CleanlabCodexLight from '@cleanlab/design-system/logos/128/cleanlab-codex-black.png'
import CleanlabCodexDark from '@cleanlab/design-system/logos/128/cleanlab-codex-white.png'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import { useEffect } from 'react'
import { useState } from 'react'

import { LandingGraphic } from '@/components/svgComponents/LandingGraphic'
import ENV_VARS from '@/global/environmentVariables'

import { CODEX_DOCS_URL } from './consts'

const ThemeToggle = () => {
  // Avoid hydration mismatch by only rendering theme switcher after mount
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const toggleTheme = () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  const { resolvedTheme, setTheme } = useTheme()

  if (!mounted) return null
  return (
    <div className="absolute right-8 top-6 z-10">
      {mounted && (
        <IconFrameButton
          icon={resolvedTheme === 'dark' ? <IconSun /> : <IconMoon />}
          aria-label="Toggle theme"
          variant="level1"
          size="small"
          onClick={toggleTheme}
        />
      )}
    </div>
  )
}

export function LandingPage({ onLogin }: { onLogin: (signup?: boolean) => Promise<void> }) {
  return (
    <div className="relative min-h-screen">
      <ThemeToggle />

      <div className="flex min-h-screen">
        {/* Content */}
        <div className="flex w-full flex-col items-center justify-center p-8 sm:w-1/2 md:px-12">
          <div className="w-content flex max-w-[520px] flex-col gap-8">
            <div className="flex max-w-2xl flex-col gap-10 text-text-primary">
              <h1>
                <span className="sr-only">Cleanlab Codex</span>
                <LogoImg
                  className="size-auto max-h-[32px] max-w-full"
                  src={{ light: CleanlabCodexLight.src, dark: CleanlabCodexDark.src }}
                  {...logoMetadata['128']['cleanlab-codex']}
                />
              </h1>
              <div className="flex flex-col gap-9">
                <div className="flex flex-col gap-5">
                  <h2 className="type-display-200 text-text-strong">Bridge the AI knowledge gap</h2>
                  <p className="type-body-300 text-text-faint">
                    Codex integrates with RAG systems to identify knowledge gaps and effectively
                    present them to subject matter experts (SMEs) for resolution.
                  </p>
                </div>
                <div className="flex flex-col gap-8">
                  <div className="flex gap-6">
                    {ENV_VARS.IS_PRODUCTION ? (
                      <Button variant="primary" size="large" asChild>
                        <Link href="https://cleanlab.ai/contact/">Request access</Link>
                      </Button>
                    ) : (
                      <Button variant="primary" size="large" onClick={() => onLogin(true)}>
                        Sign up
                      </Button>
                    )}
                    {/* <Button variant="secondary" size="large" onClick={() => onLogin()}>
                      Log in
                    </Button> */}
                  </div>
                  <LinkBlock
                    variant="secondary"
                    href={CODEX_DOCS_URL}
                    iconEnd={<IconExternalLink />}
                  >
                    Explore docs & tutorials
                  </LinkBlock>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Background graphic */}
        <LandingGraphic className="hidden sm:flex sm:w-1/2" />
      </div>
    </div>
  )
}
